import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Footer } from "../../../components/layout/Footer";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { ArrowLeft, Building2, Mail, Lock, Phone, User, FileText, Calendar, Users, Target, Upload } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useAuth } from "../../../contexts/AuthContext";
import { useLanguage } from "../../../contexts/LanguageContext";

// Define the type for project purpose
type ProjectPurpose = "sell" | "getInvestment";

// Define the type for form data
interface FormData {
  // Personal Information
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;

  // Company/Project Information
  projectPurpose: ProjectPurpose | ""; // Empty string for initial state
  companyName: string;
  projectTitle: string;
  projectDescription: string;
  teamSize: string;
  sector: string;

  // Additional Details
  website: string;
  linkedin: string;

  // Project Files
  projectFiles: File[];
  projectUrls: string[];

  // Additional Project Details
  location: string;
  assetsIncluded: string[];
}

const ProjectHolderRegister = () => {
  const [formData, setFormData] = useState<FormData>({
    // Personal Information
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",

    // Company/Project Information
    projectPurpose: "", // 'sell' or 'getInvestment'
    companyName: "",
    projectTitle: "",
    projectDescription: "",
    teamSize: "",
    sector: "",

    // Additional Details
    website: "",
    linkedin: "",

    // Project Files
    projectFiles: [],
    projectUrls: [],

    // Additional Project Details
    location: "",
    assetsIncluded: []
  });

  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { registerProjectHolder } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (!formData.fullName || !formData.email || !formData.projectTitle) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const result = await registerProjectHolder({
        name: formData.fullName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        company: formData.companyName,
        bio: formData.projectDescription, // using bio field to store project description
        location: formData.projectPurpose === 'sell' ? formData.location || undefined : undefined, // Add location only if project purpose is 'sell'
        assetsIncluded: formData.projectPurpose === 'sell' ? formData.assetsIncluded || [] : [], // Assets included only if project purpose is 'sell'
        projectName: formData.projectTitle,
        projectDescription: formData.projectDescription,
        sector: formData.sector,
        teamSize: formData.teamSize ? parseInt(formData.teamSize) : undefined,
        projectFiles: formData.projectFiles.map(file => file.name),
        projectUrls: formData.projectUrls,
        website: formData.website,
        linkedin: formData.linkedin,
        projectPurpose: formData.projectPurpose || undefined, // Add the project purpose if it exists
      });

      if (result) {
        toast({
          title: "Registration Submitted",
          description: "Your application has been submitted for review. We'll notify you once approved.",
        });

        // Navigate to project holder waiting approval page
        navigate("/waiting-approval/project-holder");
      } else {
        throw new Error("Registration failed");
      }
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: error instanceof Error ? error.message : "Failed to submit registration",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProjectPurposeChange = (value: string) => {
    handleSelectChange('projectPurpose', value);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setFormData(prev => ({
        ...prev,
        projectFiles: [...prev.projectFiles, ...files]
      }));

      toast({
        title: "Files Added",
        description: `${files.length} file(s) added to your project`,
      });
    }
  };

  const handleUrlAdd = () => {
    const urlInput = prompt("Enter the URL for your project (video, image, etc.):");
    if (urlInput && urlInput.trim()) {
      setFormData(prev => ({
        ...prev,
        projectUrls: [...prev.projectUrls, urlInput.trim()]
      }));

      toast({
        title: "URL Added",
        description: "Project URL has been added",
      });
    }
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      projectFiles: prev.projectFiles.filter((_, i) => i !== index)
    }));
  };

  const removeUrl = (index: number) => {
    setFormData(prev => ({
      ...prev,
      projectUrls: prev.projectUrls.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t("common.backToHome")}
          </Link>

          <Card className="border-border shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {t("auth.projectholder.register.title")}
              </CardTitle>
              <CardDescription>
                {t("auth.projectholder.register.subtitle")}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information Section */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {t("auth.projectholder.register.personalInfo")}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">{t("auth.projectholder.register.fullName")}</Label>
                      <div className="relative">
                        <Input
                          id="fullName"
                          name="fullName"
                          type="text"
                          placeholder={t("auth.projectholder.register.fullName")}
                          value={formData.fullName}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <User className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">{t("auth.projectholder.register.email")}</Label>
                      <div className="relative">
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="example@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Mail className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">{t("auth.projectholder.register.phone")}</Label>
                      <div className="relative">
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder={t("auth.projectholder.register.phone")}
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                        />
                        <Phone className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="companyName">{t("auth.projectholder.register.companyName")}</Label>
                      <div className="relative">
                        <Input
                          id="companyName"
                          name="companyName"
                          type="text"
                          placeholder={t("auth.projectholder.register.companyName")}
                          value={formData.companyName}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                        />
                        <Building2 className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Security Section */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    {t("auth.projectholder.register.accountSecurity")}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">{t("auth.projectholder.register.password")}</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          placeholder={t("auth.projectholder.register.password")}
                          value={formData.password}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Lock className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">{t("auth.projectholder.register.confirmPassword")}</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          placeholder={t("auth.projectholder.register.confirmPassword")}
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Lock className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Purpose Selection */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    {t("auth.projectholder.register.projectInfo")}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <Label htmlFor="projectPurpose">{t("projectPurpose.title") || "Project Purpose"}</Label>
                    <Select value={formData.projectPurpose} onValueChange={handleProjectPurposeChange} required>
                      <SelectTrigger id="projectPurpose" className="w-full">
                        <SelectValue placeholder={t("projectPurpose.selectPurpose") || "Select your project purpose"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sell">{t("projectPurpose.wantToSell") || "I want to sell my project"}</SelectItem>
                        <SelectItem value="getInvestment">{t("projectPurpose.wantToGetInvestment") || "I want to get investment in my project"}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      {t("projectPurpose.purposeDescription") || "Choose whether you want to sell your project or get investment in it"}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="projectTitle">{t("auth.projectholder.register.projectTitle")}</Label>
                      <div className="relative">
                        <Input
                          id="projectTitle"
                          name="projectTitle"
                          type="text"
                          placeholder={t("auth.projectholder.register.projectTitle")}
                          value={formData.projectTitle}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <FileText className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="projectDescription">{t("auth.projectholder.register.projectDescription")}</Label>
                      <Textarea
                        id="projectDescription"
                        name="projectDescription"
                        placeholder={t("auth.projectholder.register.projectDescription")}
                        value={formData.projectDescription}
                        onChange={handleInputChange}
                        className="min-h-[120px]"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sector">{t("auth.projectholder.register.sector")}</Label>
                        <div className="relative">
                          <Input
                            id="sector"
                            name="sector"
                            type="text"
                            placeholder={t("auth.projectholder.register.sector")}
                            value={formData.sector}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                            required
                          />
                          <Target className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="teamSize">{t("auth.projectholder.register.teamSize")}</Label>
                        <div className="relative">
                          <Input
                            id="teamSize"
                            name="teamSize"
                            type="number"
                            placeholder={t("auth.projectholder.register.teamSize")}
                            value={formData.teamSize}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <Users className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>
                    </div>


                  </div>
                </div>

                {/* Project Details Section - Only show if project purpose is 'sell' */}
                {formData.projectPurpose === 'sell' && (
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      {t("auth.projectholder.register.projectDetails")}
                    </h3>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="location">{t("auth.projectholder.register.location")}</Label>
                        <div className="relative">
                          <Input
                            id="location"
                            name="location"
                            type="text"
                            placeholder={t("auth.projectholder.register.location")}
                            value={formData.location}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <Target className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>{t("auth.projectholder.register.assetsIncluded")}</Label>
                        <div className="space-y-2">
                          {[
                            { id: "land", label: t("auth.projectholder.register.assets.land") || "Land" },
                            { id: "buildings", label: t("auth.projectholder.register.assets.buildings") || "Buildings" },
                            { id: "equipment", label: t("auth.projectholder.register.assets.equipment") || "Equipment" },
                            { id: "intellectualProperty", label: t("auth.projectholder.register.assets.intellectualProperty") || "Intellectual Property" },
                            { id: "inventory", label: t("auth.projectholder.register.assets.inventory") || "Inventory" },
                            { id: "other", label: t("auth.projectholder.register.assets.other") || "Other" },
                          ].map((asset) => (
                            <div key={asset.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`asset-${asset.id}`}
                                checked={formData.assetsIncluded.includes(asset.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData(prev => ({
                                      ...prev,
                                      assetsIncluded: [...prev.assetsIncluded, asset.id]
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      assetsIncluded: prev.assetsIncluded.filter(id => id !== asset.id)
                                    }));
                                  }
                                }}
                                className="h-4 w-4"
                              />
                              <Label htmlFor={`asset-${asset.id}`} className="text-sm font-normal cursor-pointer">
                                {asset.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>


                    </div>
                  </div>
                )}

                {/* Project Files Section */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    {t("auth.projectholder.register.projectFiles")}
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t("auth.projectholder.register.uploadFiles")}</Label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov,.avi"
                          onChange={handleFileUpload}
                          multiple
                          className="flex-1"
                        />

                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t("auth.projectholder.register.fileTypes")}
                      </p>
                    </div>

                    {formData.projectFiles.length > 0 && (
                      <div className="space-y-2">
                        <Label>{t("auth.projectholder.register.uploadedFiles")}:</Label>
                        <ul className="space-y-1">
                          {formData.projectFiles.map((file, index) => (
                            <li key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                              <span className="text-sm">{file.name}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFile(index)}
                              >
                                {t("common.remove")}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>{t("auth.projectholder.register.projectUrls")}</Label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          type="url"
                          placeholder="https://youtube.com/watch?v=..."
                          className="flex-1"
                        />

                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t("auth.projectholder.register.urlTypes")}
                      </p>
                    </div>

                    {formData.projectUrls.length > 0 && (
                      <div className="space-y-2">
                        <Label>{t("auth.projectholder.register.addedUrls")}:</Label>
                        <ul className="space-y-1">
                          {formData.projectUrls.map((url, index) => (
                            <li key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-500 hover:underline break-all"
                              >
                                {url}
                              </a>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeUrl(index)}
                              >
                                {t("common.remove")}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="pb-6">
                  <h3 className="text-lg font-semibold mb-4">{t("auth.projectholder.register.additionalInfo")}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="website">{t("auth.projectholder.register.website")}</Label>
                      <Input
                        id="website"
                        name="website"
                        type="url"
                        placeholder="https://yourwebsite.com"
                        value={formData.website}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="linkedin">{t("auth.projectholder.register.linkedin")}</Label>
                      <Input
                        id="linkedin"
                        name="linkedin"
                        type="url"
                        placeholder="https://linkedin.com/in/yourprofile"
                        value={formData.linkedin}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    type="submit"
                    className="w-full sm:w-auto bg-primary hover:bg-primary/90"
                    disabled={loading}
                  >
                    {loading ? t("common.loading") : t("auth.projectholder.register.submit")}
                  </Button>

                  <Button type="button" variant="outline" asChild className="w-full sm:w-auto">
                    <Link to="/register">
                      {t("auth.projectholder.register.back")}
                    </Link>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProjectHolderRegister;